import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheckIcon, UserPlusIcon } from '@heroicons/react/24/outline';
import AuthShell from '../components/AuthShell';
import { useAuth } from '../context/AuthContext';
import { useBranding } from '../context/BrandingContext';
import LoadingSpinner from '../components/LoadingSpinner';

function RegisterPage() {
  const { register } = useAuth();
  const branding = useBranding();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const heroFeatures = [
    {
      title: 'Launch fast',
      description: 'Quick onboarding for admins and operators without leaving the product flow.',
    },
    {
      title: 'Stay secure',
      description: 'Protected credentials and tenant-aware access across the platform surface.',
    },
    {
      title: 'Start clean',
      description: 'A focused entry point that matches the updated login experience and scales better on mobile.',
    },
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await register(email, password, confirmPassword);
    } catch (error) {
      // Error handling is done in the auth context
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthShell
      appName={branding.app_name}
      logoUrl={branding.logo_url}
      headerBadge="New account setup"
      headerIcon={<ShieldCheckIcon className="h-4 w-4 text-[var(--primary)]" />}
      title="Create your account"
      description={`Join ${branding.app_name} and get access to the vehicle booking and management workspace.`}
      heroBadge="Operator onboarding"
      heroIcon={<UserPlusIcon className="h-4 w-4 text-[var(--primary)]" />}
      heroTitle="Create access for your team and step straight into fleet operations."
      heroDescription={`Set up credentials for ${branding.app_name} and move directly into vehicles, billing, user management, and operator workflows.`}
      heroFeatures={heroFeatures}
      accentClassName="bg-[radial-gradient(circle,_rgba(59,130,246,0.18),_transparent_68%)]"
    >
      <form className="mt-6 space-y-5 sm:mt-8 sm:space-y-6" onSubmit={handleSubmit}>
                <div className="space-y-4">
                  <div>
                    <label htmlFor="email" className="mb-1 block text-sm font-medium text-on-surface">
                      Email address
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      required
                      className="input-field"
                      placeholder="Enter your email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                  </div>

                  <div>
                    <label htmlFor="password" className="mb-1 block text-sm font-medium text-on-surface">
                      Password
                    </label>
                    <input
                      id="password"
                      name="password"
                      type="password"
                      autoComplete="new-password"
                      required
                      className="input-field"
                      placeholder="Create a password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                    />
                  </div>

                  <div>
                    <label htmlFor="confirmPassword" className="mb-1 block text-sm font-medium text-on-surface">
                      Confirm Password
                    </label>
                    <input
                      id="confirmPassword"
                      name="confirmPassword"
                      type="password"
                      autoComplete="new-password"
                      required
                      className="input-field"
                      placeholder="Confirm your password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                    />
                  </div>
                </div>

                <div className="auth-subpanel p-4 text-sm leading-6 text-on-surface-variant">
                  Use a strong password so your operator access and tenant data stay protected from day one.
                </div>

                <div>
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="btn-primary flex w-full justify-center py-3 text-base disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {isLoading ? <LoadingSpinner size="sm" /> : 'Create account'}
                  </button>
                </div>

                <div className="auth-subpanel p-4 text-sm leading-6 text-on-surface-variant">
                  Already have an account?{' '}
                  <Link to="/login" className="font-semibold hover:underline" style={{ color: 'var(--primary)' }}>
                    Sign in here
                  </Link>
                </div>
      </form>
    </AuthShell>
  );
}

export default RegisterPage;